import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import {
  getCollectedMoneyRequest,
  getTransferCashOutRequest,
} from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { TransactionType, TransactionStatus } from "../../../utils/enums";
import { useToast } from "../../../contexts/ToastContext";
import "../../css/cachIn.css";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Derive broker apply mode after get-collected-money-request. */
function collectFlowApplyMode(data) {
  if (!data || typeof data !== "object") return null;
  const type = num(data.TYPE ?? data.type);
  const status = num(data.STATUS ?? data.status);
  if (type === TransactionType.CASHIN && status === TransactionStatus.PENDING) {
    return "collect_cash_in";
  }
  if (type === TransactionType.CASHOUT && status === TransactionStatus.COLLECTED) {
    return "collect_cash_out";
  }
  return null;
}

/** Derive broker apply mode after get-transfer-cash-out-request. */
function transferFlowApplyMode(data) {
  if (!data || typeof data !== "object") return null;
  const type = num(data.TYPE ?? data.type);
  const status = num(data.STATUS ?? data.status);
  if (type !== TransactionType.CASHOUT) return null;
  if (status === TransactionStatus.COMPLETED) return null;
  return "transfer_cash_out";
}

function pickCurrencyCode(row) {
  if (!row || typeof row !== "object") return "";
  const w =
    row.requester_wallet ||
    row.requesterWallet ||
    row.REQUESTER_WALLET ||
    null;
  const c = w?.currencies || w?.currency || row.currencies || null;
  return (
    c?.CURRENCY_CODE ||
    c?.currency_code ||
    row.CURRENCY_CODE ||
    ""
  );
}

function pickAmount(row) {
  if (!row) return "";
  const a = row.AMOUNT ?? row.amount;
  return a != null ? String(a) : "";
}

function statusLabel(status, t) {
  const s = num(status);
  const map = {
    [TransactionStatus.PENDING]: t("collect_tx_status_pending", { defaultValue: "Pending" }),
    [TransactionStatus.COLLECTED]: t("collect_tx_status_collected", { defaultValue: "Collected" }),
    [TransactionStatus.COMPLETED]: t("collect_tx_status_completed", { defaultValue: "Completed" }),
  };
  return map[s] != null ? map[s] : String(status ?? "-");
}

function typeLabel(type, t) {
  const ty = num(type);
  if (ty === TransactionType.CASHIN) return t("cashin", { defaultValue: "Cash In" });
  if (ty === TransactionType.CASHOUT) return t("cashout", { defaultValue: "Cash Out" });
  return String(type ?? "-");
}

export default function CollectTransaction() {
  const { t } = useTranslation("mobile");
  const { success: showSuccess, error: showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [tab, setTab] = useState(() => {
    const f = (searchParams.get("flow") || "").toLowerCase();
    return f === "transfer" ? "transfer" : "collect";
  });
  const [requestTokenInput, setRequestTokenInput] = useState(
    () => searchParams.get("token") || searchParams.get("REQUEST_TOKEN") || ""
  );

  const [requestRow, setRequestRow] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordMode, setPasswordMode] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      setLoadingUser(true);
      try {
        const stored = sessionStorage.getItem("userData");
        if (stored) {
          setUser(JSON.parse(stored));
        }
      } catch {
        setUser(null);
      }
      if (!cancelled) setLoadingUser(false);
    };
    loadUser();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const token = searchParams.get("token") || searchParams.get("REQUEST_TOKEN");
    const flow = searchParams.get("flow");
    if (token) setRequestTokenInput(token);
    if (flow === "transfer") setTab("transfer");
    if (flow === "collect") setTab("collect");
  }, [searchParams]);

  const applyMode = useMemo(() => {
    if (!requestRow) return null;
    return tab === "collect"
      ? collectFlowApplyMode(requestRow)
      : transferFlowApplyMode(requestRow);
  }, [requestRow, tab]);

  const syncQueryParams = useCallback(
    (nextTab, tokenTrim) => {
      const next = new URLSearchParams(searchParams);
      if (tokenTrim) next.set("token", tokenTrim);
      else {
        next.delete("token");
        next.delete("REQUEST_TOKEN");
      }
      next.set("flow", nextTab === "transfer" ? "transfer" : "collect");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleLoadRequest = async () => {
    const tok = String(requestTokenInput || "").trim();
    if (!tok) {
      showError(t("collect_tx_token_required", { defaultValue: "Enter a request token." }));
      return;
    }

    setLoadingRequest(true);
    setRequestRow(null);
    try {
      const res =
        tab === "collect"
          ? await getCollectedMoneyRequest(tok)
          : await getTransferCashOutRequest(tok);

      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE ?? d.code ?? 0);

      if (code !== AppConstants.responseOk && code !== 200) {
        const msg =
          code === AppConstants.responseNeedPermission || code === 204
            ? t("collect_tx_no_permission", {
                defaultValue: "No permission or invalid token.",
              })
            : code === AppConstants.responseAlreadyOk || code === 205
              ? t("collect_tx_already_handled", { defaultValue: "This request was already handled." })
              : t("collect_tx_load_failed", { defaultValue: "Could not load this request." });
        showError(msg);
        return;
      }

      const row = d.DATA ?? d.data ?? null;
      if (!row) {
        showError(t("collect_tx_no_data", { defaultValue: "No request data returned." }));
        return;
      }

      setRequestRow(row);
      syncQueryParams(tab, tok);

      const mode =
        tab === "collect" ? collectFlowApplyMode(row) : transferFlowApplyMode(row);
      if (!mode) {
        showError(
          t("collect_tx_wrong_state", {
            defaultValue:
              tab === "collect"
                ? "This request is not in a state that can be collected here."
                : "This request cannot be transferred from this screen.",
          })
        );
      }
    } catch (e) {
      console.error(e);
      showError(t("collect_tx_load_failed", { defaultValue: "Could not load this request." }));
    } finally {
      setLoadingRequest(false);
    }
  };

  const openConfirm = () => {
    if (!applyMode || !requestRow) return;
    setPasswordMode(applyMode);
    setShowPasswordPopup(true);
  };

  const firstMessage = useMemo(() => {
    if (!requestRow) return "";
    const amt = pickAmount(requestRow);
    const cur = pickCurrencyCode(requestRow);
    const typ = typeLabel(requestRow.TYPE ?? requestRow.type, t);
    return t("collect_tx_confirm_summary", {
      defaultValue: "{{type}} — {{amount}} {{currency}}",
      type: typ,
      amount: amt || "—",
      currency: cur || "",
    });
  }, [requestRow, t]);

  const locked = loadingUser || !user;

  return (
    <div className="ib-dashboard-page collect-transaction-page cash-in-page">
      <Sidebar user={user} />
      <div className="ib-dashboard-main cash-in-content">
        {loadingUser && (
          <div className="cash-in-loading-message">{t("loading")}…</div>
        )}

        <div className="cash-in-container cash-in-container--styled">
          <div className="cash-in-tab-content cash-in-tab-content--styled">
            {locked ? (
              <div className="cash-in-locked-message">
                {t("loading")}…
              </div>
            ) : (
              <>
                <div className="cash-in-layout-grid collect-tx-grid">
                  <div className="cash-in-form-panel collect-tx-form-panel">
                    <div className="cash-in-panel-header">
                      <h2>{t("collect_transaction", { defaultValue: "Collect Transaction" })}</h2>
                      <p>
                        {t("collect_transaction_description", {
                          defaultValue: "Collect cash in or cash out transactions from clients",
                        })}
                      </p>
                    </div>

                    <div role="tablist" className="collect-tx-tabs" aria-label="Flow">
                      <button
                        type="button"
                        className={`collect-tx-tab${tab === "collect" ? " collect-tx-tab--active" : ""}`}
                        onClick={() => {
                          setTab("collect");
                          setRequestRow(null);
                          syncQueryParams("collect", String(requestTokenInput || "").trim());
                        }}
                      >
                        {t("collect_tx_tab_collect", { defaultValue: "Collect money" })}
                      </button>
                      <button
                        type="button"
                        className={`collect-tx-tab${tab === "transfer" ? " collect-tx-tab--active" : ""}`}
                        onClick={() => {
                          setTab("transfer");
                          setRequestRow(null);
                          syncQueryParams("transfer", String(requestTokenInput || "").trim());
                        }}
                      >
                        {t("collect_tx_tab_transfer", { defaultValue: "Transfer cash-out" })}
                      </button>
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label" htmlFor="collect-tx-token">
                        {t("collect_tx_request_token", { defaultValue: "Request token" })}
                      </label>
                      <CustomInput
                        id="collect-tx-token"
                        className="cash-in-input collect-tx-token-input"
                        value={requestTokenInput}
                        onChange={(e) => setRequestTokenInput(e.target.value)}
                        placeholder={t("collect_tx_token_placeholder", {
                          defaultValue: "Client request token",
                        })}
                        disabled={loadingRequest}
                        autoComplete="off"
                      />
                    </div>

                    <div className="collect-tx-actions">
                      <CustomButton
                        type="button"
                        variant="secondary"
                        className="custom-button collect-tx-btn-secondary"
                        onClick={handleLoadRequest}
                        disabled={loadingRequest}
                      >
                        {loadingRequest
                          ? t("loading")
                          : t("collect_tx_load_request", { defaultValue: "Load request" })}
                      </CustomButton>
                      <CustomButton
                        type="button"
                        variant="primary"
                        className="custom-button cash-in-proceed-button collect-tx-btn-primary"
                        onClick={openConfirm}
                        disabled={loadingRequest || !applyMode || !requestRow}
                      >
                        {t("collect_tx_confirm_pin", {
                          defaultValue: "Confirm with client PIN",
                        })}
                      </CustomButton>
                    </div>

                    {requestRow && (
                      <div
                        className="cash-in-info-card"
                        style={{ marginTop: 24, textAlign: "left" }}
                      >
                        <ul className="cash-in-processing-list" style={{ paddingLeft: 18 }}>
                          <li>
                            <strong>{t("type", { defaultValue: "Type" })}:</strong>{" "}
                            {typeLabel(requestRow.TYPE ?? requestRow.type, t)}
                          </li>
                          <li>
                            <strong>{t("amount", { defaultValue: "Amount" })}:</strong>{" "}
                            {pickAmount(requestRow)} {pickCurrencyCode(requestRow)}
                          </li>
                          <li>
                            <strong>{t("status", { defaultValue: "Status" })}:</strong>{" "}
                            {statusLabel(requestRow.STATUS ?? requestRow.status, t)}
                          </li>
                          <li>
                            <strong>{t("collect_tx_endpoint_hint", { defaultValue: "API" })}:</strong>{" "}
                            {tab === "collect"
                              ? "get-collected-money-request"
                              : "get-transfer-cash-out-request"}
                          </li>
                        </ul>
                        {!applyMode && (
                          <p style={{ marginTop: 12, opacity: 0.9 }}>
                            {t("collect_tx_cannot_apply", {
                              defaultValue:
                                "This request is not in the correct status for this action.",
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="cash-in-side-panel collect-tx-side">
                    <div className="cash-in-info-card collect-tx-help-card">
                      <h3>
                        <i className="fa-solid fa-circle-info" aria-hidden />
                        {t("collect_tx_help_title", { defaultValue: "How it works" })}
                      </h3>
                      <p className="collect-tx-help-body">
                        {t("collect_tx_help_body", {
                          defaultValue:
                            "Enter the request token from the client's request, review the details, then enter the client's password to confirm. All steps run here in the portal.",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <TransactionPasswordDialog
        open={showPasswordPopup}
        mode={passwordMode || "collect_cash_in"}
        requestToken={
          requestRow?.REQUEST_TOKEN != null ? String(requestRow.REQUEST_TOKEN) : String(requestTokenInput || "").trim()
        }
        firstMessage={firstMessage}
        secondMessage={t("collect_tx_client_pin_note", {
          defaultValue: "Enter the client's (requestor's) password.",
        })}
        onClose={(result, payload) => {
          setShowPasswordPopup(false);
          setPasswordMode(null);

          if (result === "ok") {
            showSuccess(
              t("collect_tx_success", { defaultValue: "Operation completed successfully." })
            );
            setRequestRow(null);
            setRequestTokenInput("");
            syncQueryParams(tab, "");
            return;
          }
          if (result === "failed" && payload?.responseData) {
            const d = payload.responseData;
            const code = Number(d.CODE ?? d.code ?? 0);
            if (code === AppConstants.responseFailed || code === 207) {
              showError(
                d.MESSAGE ||
                  d.message ||
                  t("collect_tx_failed", { defaultValue: "Operation failed." })
              );
              return;
            }
          }
        }}
      />
    </div>
  );
}
